import type { FieldSet, ValidationResult } from "./Form";
import type { FieldKind } from "../../services/ChatService";

/**
 * Live handles to the form-kit inputs currently mounted, keyed by their `data-field-id`.
 *
 * The AI form bridge (`src/ui/aichat/forms/`, see "AI form bridge" in CLAUDE.md) discovers
 * fields from the rendered DOM and, when the element's `data-field-id` has an entry here,
 * reads model-space values, option objects and validators from the handle instead of the
 * DOM, and applies values through the input's own change path. The registry is inert on
 * its own: nothing reads it unless the bridge is active, and a missing or stale entry only
 * degrades a field to DOM behaviour.
 *
 * Class inputs register themselves from `AbstractInputElement`'s mount/unmount next to the
 * existing `setInitialModel` / `removeValidation` calls; `SearchableSelectInput` does the
 * same from its mount effect. Only `Map.set` / `Map.delete` run in those lifecycles, so a
 * handle can never break a form.
 */

/** The field kinds the chat wire contract understands; anything else is reported as unsupported. */
export type KitFieldKind = FieldKind | "unsupported";

/** One choice of a select-like input: the option-space value (what the DOM option carries) and its label. */
export interface KitFieldOption {
  value: unknown;
  label: string;
}

export interface KitField {
  readonly fieldId: string;
  /**
   * The model path inside the fieldSet's model object; undefined for an input mounted without one.
   * Inside a list item it is relative to the item — prepend `fieldSet.modelPrefix` for the form-level path.
   */
  readonly model: string | undefined;
  /** The `data-form-id` of the form this input belongs to; inside a list item, the item reports its page form's id. */
  readonly formId: string | undefined;
  /** The fieldSet the input is bound to; a list item's carries `modelPrefix` (e.g. `translations[1]`). */
  readonly fieldSet: FieldSet;
  kind(): KitFieldKind;
  /** The full option list for select-like kinds, undefined for the others. */
  options(): KitFieldOption[] | undefined;
  /** Model-space value for plain inputs, option-space value for select-like inputs, percent space for FractionInput. */
  currentValue(): unknown;
  isRequired(): boolean;
  isReadOnly(): boolean;
  /**
   * Runs the input's validators (required + `validators`) against a candidate value without
   * touching the form. `pristine` exists only because the class inputs share this signature
   * with their own `validate`; omit it to validate as a user-entered value.
   */
  validate(value: unknown, pristine?: boolean): ValidationResult;
  /** Sets the value exactly as a user change would: parse, validate, then the page's `formDidUpdate`. */
  apply(value: unknown): void;
}

const kitFields = new Map<string, KitField>();
let fieldCounter = 0;
let formCounter = 0;

export function nextKitFieldId(): string {
  fieldCounter += 1;
  return `kf-${fieldCounter}`;
}

export function nextKitFormId(): string {
  formCounter += 1;
  return `kform-${formCounter}`;
}

export function registerKitField(field: KitField): void {
  kitFields.set(field.fieldId, field);
}

export function unregisterKitField(fieldId: string): void {
  kitFields.delete(fieldId);
}

export function getKitField(fieldId: string): KitField | undefined {
  return kitFields.get(fieldId);
}

export function kitFieldsForForm(formId: string): KitField[] {
  return Array.from(kitFields.values()).filter((field) => field.formId === formId);
}
